import React, { useState, useEffect } from "react";
import { DialogWrapper, DialogWrapperProps } from "components/shared/dialog";
import { Box, IconButton, Tab } from "@material-ui/core";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import { RegularFileImport } from "./regular-import";
import { EmbededFileImport } from "./embeded-import";
import { UploadType } from "store/url-builder";
import { uploadContent, uploadContentEmbeded } from "adapters";
import { FileReader } from "components/shared/file-reader";
import { HighlightOffOutlined } from "@material-ui/icons";
import ImageIcon from "@material-ui/icons/Image";
import { theme } from "material-theme";
import { uploadFile } from "adapters/public-card";
import { useParams } from "react-router-dom";
import { Input } from "components/shared/input";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  InstagramEmbed,
  FacebookEmbed,
  TikTokEmbed,
  XEmbed,
  PinterestEmbed,
} from "react-social-media-embed";
import {
  isInstagram,
  isFacebookReel,
  isFacebookVideo,
  isTikTok,
  isTwitter,
  isLinkedIn,
  isPinterest,
  getLinkedInEmbedUrl,
} from "utils";

const urlSchema = yup.string().url().nullable().optional();

export interface FileImportDialogProps extends DialogWrapperProps {
  onFileUploaded(file: UploadType): void;
  onFileRemoved?(): void;
  preselectfile?: UploadType;
}

export const FileImportDialog = (props: FileImportDialogProps) => {
  const [tab, setTab] = useState("1");
  const [selectedFile, setSelectedFile] = useState<UploadType | File>();
  const [url, setUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string>("");
  const [externalImageUrl, setExternalImageUrl] = useState<string>("");
  const [externalImageUrlError, setExternalImageUrlError] = useState<string>("");
  const [externalImagePreviewUrl, setExternalImagePreviewUrl] =
    useState<string>("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { cardId }: any = useParams();
  const preselectUrl = props.preselectfile?.fileUrl || "";
  const isInstagramUrl = preselectUrl && isInstagram(preselectUrl);
  const isFacebookReelUrl = preselectUrl && isFacebookReel(preselectUrl);
  const isFacebookVideoUrl = preselectUrl && isFacebookVideo(preselectUrl);
  const isFacebookUrl = isFacebookReelUrl || isFacebookVideoUrl;
  const isTikTokUrl = preselectUrl && isTikTok(preselectUrl);
  const isTwitterUrl = preselectUrl && isTwitter(preselectUrl);
  const isLinkedInUrl = preselectUrl && isLinkedIn(preselectUrl);
  const isPinterestUrl = preselectUrl && isPinterest(preselectUrl);
  const isSocialEmbedUrl =
    isInstagramUrl ||
    isTikTokUrl ||
    isTwitterUrl ||
    isLinkedInUrl ||
    isPinterestUrl ||
    isFacebookUrl;

  // Initialize URL from preselectfile when editing
  useEffect(() => {
    if (props.preselectfile && (props.preselectfile as any).redirectLink) {
      setUrl((props.preselectfile as any).redirectLink);
      setUrlError("");
    }
  }, [props.preselectfile]);

  const getRedirectLinkPatch = () => {
    if (url === null || url === undefined) return null;
    if (url.trim() === "") return { redirectLink: "" };
    if (urlError) return null;
    return { redirectLink: url };
  };

  const handleUrlChange = async (value: string) => {
    setUrl(value);
    if (value.trim() === "") {
      setUrlError("");
      return;
    }

    try {
      await urlSchema.validate(value);
      setUrlError("");
    } catch (error: any) {
      setUrlError(error.message || "Please enter a valid URL");
    }
  };

  const handleExternalImageUrlChange = async (value: string) => {
    setExternalImageUrl(value);
    if (value.trim() === "") {
      setExternalImageUrlError("");
      setExternalImagePreviewUrl("");
      setIsPreviewLoading(false);
      return;
    }

    try {
      await urlSchema.validate(value);
      setExternalImageUrlError("");
    } catch (error: any) {
      setExternalImageUrlError("Please enter a valid image URL");
    }
  };

  useEffect(() => {
    let isActive = true;

    if (!externalImageUrl || externalImageUrlError) {
      setExternalImagePreviewUrl("");
      setIsPreviewLoading(false);
      return;
    }

    setIsPreviewLoading(true);
    const image = new Image();
    image.onload = () => {
      if (!isActive) return;
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (width < 240) {
        setExternalImageUrlError("Image width is too small");
        setExternalImagePreviewUrl("");
      } else if (height < 240) {
        setExternalImageUrlError("Image height is too small");
        setExternalImagePreviewUrl("");
      } else if (width > 2048) {
        setExternalImageUrlError("Image width is too high");
        setExternalImagePreviewUrl("");
      } else if (height > 2048) {
        setExternalImageUrlError("Image height is too high");
        setExternalImagePreviewUrl("");
      } else {
        setExternalImageUrlError("");
        setExternalImagePreviewUrl(externalImageUrl);
      }
      setIsPreviewLoading(false);
    };
    image.onerror = () => {
      if (!isActive) return;
      setExternalImageUrlError("Unable to load image preview");
      setExternalImagePreviewUrl("");
      setIsPreviewLoading(false);
    };
    image.src = externalImageUrl;

    return () => {
      isActive = false;
    };
  }, [externalImageUrl, externalImageUrlError]);

  const getImageDimensions = (blob: Blob) =>
    new Promise<{ width: number; height: number }>((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(blob);
      image.onload = () => {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        URL.revokeObjectURL(objectUrl);
        resolve({ width, height });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Unable to read image dimensions"));
      };
      image.src = objectUrl;
    });

  const getFileNameFromUrl = (imageUrl: string, mimeType: string) => {
    let fileName = "external-image";
    try {
      const urlObject = new URL(imageUrl);
      const lastSegment = urlObject.pathname.split("/").filter(Boolean).pop();
      if (lastSegment) fileName = lastSegment;
    } catch (error) {
      // keep default name
    }

    if (!/\.[a-z0-9]+$/i.test(fileName)) {
      const extensionMap: Record<string, string> = {
        "image/jpeg": "jpg",
        "image/png": "png",
        "image/gif": "gif",
        "image/webp": "webp",
        "image/svg+xml": "svg",
        "image/bmp": "bmp",
      };
      const extension = extensionMap[mimeType] || "jpg";
      fileName = `${fileName}.${extension}`;
    }

    return fileName.replace(/[^\w.-]/g, "-");
  };

  const downloadExternalImage = async (imageUrl: string) => {
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error("Unable to download image");

    const blob = await response.blob();
    const mimeType = blob.type || response.headers.get("content-type") || "";
    if (!mimeType.startsWith("image/")) {
      throw new Error("Please enter a valid image URL");
    }

    const maxSizeInBytes = 5 * 1024 * 1024;
    if (blob.size > maxSizeInBytes) {
      throw new Error("The attachment size exceeds the allowable limit");
    }

    const { width, height } = await getImageDimensions(blob);
    if (width < 240) throw new Error("Image width is too small");
    if (height < 240) throw new Error("Image height is too small");
    if (width > 2048) throw new Error("Image width is too high");
    if (height > 2048) throw new Error("Image height is too high");

    const fileName = getFileNameFromUrl(imageUrl, mimeType);
    return new File([blob], fileName, { type: mimeType });
  };

  const uploadImageFile = async (file: File) => {
    const metadataFile = new File([""], file.name, { type: file.type });
    const contentResponse = await uploadContent(metadataFile);
    if (!contentResponse.data.success) {
      throw new Error("Unable to upload image metadata");
    }

    const redirectLinkPatch = getRedirectLinkPatch();
    const dataWithUrl = {
      ...contentResponse.data,
      data: {
        ...contentResponse.data.data,
        ...(redirectLinkPatch || {}),
      },
    };

    const uploadResponse = await uploadFile(cardId, file, dataWithUrl);
    if (!uploadResponse.data.success) {
      throw new Error("Unable to upload image");
    }

    const fileWithUrl = {
      ...uploadResponse.data.data,
      ...(redirectLinkPatch || {}),
    } as UploadType;
    props.onFileUploaded(fileWithUrl);
  };

  const submit = async () => {
    // When editing, allow submission even without selecting a new file
    // Just update the URL if provided
    if (!selectedFile && !props.preselectfile && tab !== "3") return;

    // If editing and no new file selected, just update the existing file with new URL
    if (props.preselectfile && !selectedFile) {
      const redirectLinkPatch = getRedirectLinkPatch();
      const updatedFile = {
        ...props.preselectfile,
        ...(redirectLinkPatch || {}),
      } as UploadType;
      props.onFileUploaded(updatedFile);
      return;
    }

    try {
      setIsSubmitting(true);
      switch (tab) {
        case "1": {
          if (!(selectedFile instanceof File)) return;
          await uploadImageFile(selectedFile);
          break;
        }
        case "2": {
          const redirectLinkPatch = getRedirectLinkPatch();
          const embededData = {
            ...(selectedFile as UploadType),
            ...(redirectLinkPatch || {}),
          };
          const response = await uploadContentEmbeded(embededData);
          if (response.status === 200) {
            const fileWithUrl = {
              ...response.data.data,
              ...(redirectLinkPatch || {}),
            } as UploadType;
            props.onFileUploaded(fileWithUrl);
          }
          break;
        }
        case "3": {
          if (!externalImageUrl || externalImageUrlError) {
            setExternalImageUrlError("Please enter a valid image URL");
            return;
          }
          const downloadedFile = await downloadExternalImage(externalImageUrl);
          await uploadImageFile(downloadedFile);
          break;
        }
        default:
          break;
      }
    } catch (error: any) {
      const message =
        error?.message || "Unable to import image from external URL";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      {...props}
      title={props.title || "Import file"}
      onSubmit={submit}
      loading={isSubmitting}
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return false;
        if (props.onClose) props?.onClose(event, reason);
      }}
    >
      {props.preselectfile &&
        props.preselectfile.mimeType !== "Empty placeholder" && (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: isSocialEmbedUrl ? "auto" : "20em",
                minHeight: "20em",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: isSocialEmbedUrl ? "visible" : "hidden",
                marginBottom: 2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  zIndex: 2,
                  right: 0,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <IconButton onClick={props.onFileRemoved}>
                  <HighlightOffOutlined
                    style={{ color: theme.palette.error.main }}
                  />
                </IconButton>
              </Box>
              {isSocialEmbedUrl ? (
                isInstagramUrl ? (
                  <div className="social-embed-preview">
                    <InstagramEmbed url={preselectUrl} width={328} />
                  </div>
                ) : isTikTokUrl ? (
                  <div className="social-embed-preview">
                    <TikTokEmbed url={preselectUrl} width={325} />
                  </div>
                ) : isTwitterUrl ? (
                  <div className="social-embed-preview social-embed-preview--twitter">
                    <XEmbed url={preselectUrl} width={325} />
                  </div>
                ) : isLinkedInUrl ? (
                  <div className="social-embed-preview social-embed-preview--linkedin">
                    <iframe
                      title="LinkedIn embed"
                      src={getLinkedInEmbedUrl(preselectUrl)}
                      width="100%"
                      height="700"
                      frameBorder="0"
                      allowFullScreen
                    />
                  </div>
                ) : isPinterestUrl ? (
                  <div className="social-embed-preview">
                    <PinterestEmbed url={preselectUrl} width={325} />
                  </div>
                ) : (
                  <div className="social-embed-preview">
                    <FacebookEmbed url={preselectUrl} width={280} />
                  </div>
                )
              ) : (
                <FileReader
                  file={props.preselectfile}
                  videoConfig={{ controls: true }}
                />
              )}
            </Box>
            <Box sx={{ marginTop: 2, width: "100%" }}>
              <Input
                className="mb-3"
                label="Optional: Redirect URL (opens on click)"
                onChange={(e: any) => handleUrlChange(e.currentTarget.value)}
                value={url || ""}
                error={!!urlError}
                helperText={urlError}
                fullWidth
                placeholder="https://example.com"
              />
            </Box>
          </Box>
        )}

      {(!props.preselectfile ||
        props.preselectfile.mimeType === "Empty placeholder") && (
        <Box sx={{ width: "100%" }}>
          <TabContext value={tab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={(e, newTab) => setTab(newTab)}
                centered
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
              >
                <Tab
                  label="Upload Image"
                  value="1"
                  style={{ fontSize: "0.9rem", minWidth: 0, padding: "6px 8px" }}
                />
                <Tab
                  label="Public Video URL"
                  value="2"
                  style={{ fontSize: "0.9rem", minWidth: 0, padding: "6px 8px" }}
                />
                <Tab
                  label="Import Image URL"
                  value="3"
                  style={{ fontSize: "0.9rem", minWidth: 0, padding: "6px 8px" }}
                />
              </TabList>
            </Box>
            <TabPanel value="1">
              <RegularFileImport
                onFileChanged={setSelectedFile}
                onUrlChanged={setUrl}
              />
            </TabPanel>
            <TabPanel value="2">
              <EmbededFileImport
                onFileChanged={setSelectedFile}
                onUrlChanged={setUrl}
              />
            </TabPanel>
            <TabPanel value="3">
              <Box
                sx={{ paddingBottom: 10, fontStyle: "italic" }}
                style={{ opacity: 0.7 }}
              >
                Paste a public image URL. The image will be downloaded and
                uploaded to your gallery.
              </Box>
              <Input
                className="mb-3"
                label="Image URL"
                onChange={(e: any) =>
                  handleExternalImageUrlChange(e.currentTarget.value)
                }
                value={externalImageUrl}
                error={!!externalImageUrlError}
                helperText={externalImageUrlError}
                fullWidth
                placeholder="https://example.com/image.jpg"
              />
              <Box
                sx={{
                  height: "100%",
                  minHeight: "20em",
                  border: "2px dashed #C4C4C4",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                  marginBottom: 2,
                }}
              >
                {isPreviewLoading ? (
                  <Box style={{ opacity: 0.6 }}>Loading preview...</Box>
                ) : externalImagePreviewUrl ? (
                  <img
                    src={externalImagePreviewUrl}
                    alt="External preview"
                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                  />
                ) : (
                  <ImageIcon fontSize="large" style={{ opacity: 0.6 }} />
                )}
              </Box>
              <Input
                className="mb-3"
                label="Optional: Redirect URL (opens on click)"
                onChange={(e: any) => handleUrlChange(e.currentTarget.value)}
                value={url || ""}
                error={!!urlError}
                helperText={urlError}
                fullWidth
                placeholder="https://example.com"
              />
            </TabPanel>
          </TabContext>
        </Box>
      )}
    </DialogWrapper>
  );
};
